/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: pagingGroupsActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Applications module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../config/api';

/**
 * Shared helpers for Paging Groups (Paging & Intercom) module actions.
 */

/**
 * Build the URL used to duplicate a Paging Group in the form.
 * Pattern: /pbx/applications/paging/new?duplicate={id}
 */
export const getPagingGroupDuplicateUrl = (id: string | number): string =>
  `/pbx/applications/paging/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given paging group id.
 */
export const navigateToPagingGroupDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getPagingGroupDuplicateUrl(id));
};

/**
 * Low-level API helper to delete a Paging Group by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deletePagingGroupById = async (id: string | number): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`/api/telephony/paging-groups/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete paging group';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};


